import { FormikHelpers, FormikValues, withFormik, WithFormikConfig } from 'formik'

/**
 * Simply a safe wrapper around Formik's withFormik that:
 * - Keeps all original config (validation, mapping, etc.)
 * - resetForm() on submit error (configurable) and setSubmitting(false) runs to skip doing the same thing in many forms
 * - Extra onHandlers ready to be added if necessary
 */

export function withCustomFormik<Props extends object, Values extends FormikValues>(
  config: WithFormikConfig<Props, Values> & {
    resetOnError?: boolean
    onError?: (err: unknown, formikHelpers: FormikHelpers<Values>) => void
  }
) {
  const {
    handleSubmit: originalHandleSubmit,
    resetOnError = false,
    onError,
    ...restConfig
  } = config

  return withFormik<Props, Values>({
    ...restConfig,
    handleSubmit: async (values, formikBag) => {
      const { setSubmitting, resetForm } = formikBag as FormikHelpers<Values>

      try {
        await originalHandleSubmit(values, formikBag)
      } catch (err) {
        console.error('[Formik submit error]: ', err)

        // Custom error handling
        if (onError) {
          onError(err, formikBag)
        } else if (resetOnError) {
          // Always reset form by default if custom error handling wasn't passed
          resetForm()
        }
      } finally {
        setSubmitting(false)
      }
    },
  })
}
